import { Canvas } from './Canvas';
import { Input } from './Input';
import { Screen, ScreenContext } from './screens/Screen';
import { TitleScreen } from './screens/TitleScreen';
import { MainMenu } from './screens/MainMenu';
import { RecruitScreen } from './screens/RecruitScreen';
import { Shop } from './screens/Shop';
import { MageShop } from './screens/MageShop';
import { TrainingSchool } from './screens/TrainingSchool';
import { BattleSetup } from './screens/BattleSetup';
import { Battle } from './screens/Battle';
import { TeamsScreen } from './screens/TeamsScreen';
import { Settings } from './screens/Settings';
import { BattleSummary } from './screens/BattleSummary';
import { KillLeaderboard } from './screens/KillLeaderboard';
import { AssetManifest, ScreenState } from './types';
import { loadAllAssets } from './utils/assets';
import { GameState, createGameState, startNewGame, progressToNextMonth, isBattleMonth } from './systems/GameState';
import { runAIBattles } from './systems/AIBattle';
import { saveGame, loadGame, hasSaveGame } from './systems/SaveLoad';

// Interface for screens that need game state
interface GameStateScreen extends Screen {
  setGameState(gameState: GameState): void;
}

function isGameStateScreen(screen: Screen): screen is GameStateScreen {
  return 'setGameState' in screen;
}

export class Game {
  private canvas!: Canvas;
  private input!: Input;
  private assets!: AssetManifest;
  private screens: Map<ScreenState, Screen> = new Map();
  private currentScreen: Screen | null = null;
  private lastFrameTime: number = 0;
  private isRunning: boolean = false;
  private gameState: GameState | null = null;

  async init(): Promise<void> {
    console.log('Initializing game...');

    // Get canvas element
    const canvasElement = document.getElementById('game') as HTMLCanvasElement;
    if (!canvasElement) {
      throw new Error('Canvas element not found');
    }

    // Initialize canvas and input
    this.canvas = new Canvas(canvasElement);
    this.input = new Input(canvasElement);

    // Load all assets
    this.assets = await loadAllAssets();

    // Create screen context
    const screenContext: ScreenContext = {
      assets: this.assets,
      input: this.input,
      onScreenChange: this.changeScreen.bind(this),
      onStartNewGame: this.startNewGame.bind(this),
      onProgressMonth: this.progressMonth.bind(this),
      onLoadGame: this.loadSavedGame.bind(this),
      onSaveGame: this.saveCurrentGame.bind(this),
      hasSaveGame: () => hasSaveGame(),
    };

    // Initialize all screens
    this.screens.set(ScreenState.TITLESCREEN, new TitleScreen(screenContext));
    this.screens.set(ScreenState.MAIN_MENU, new MainMenu(screenContext, this.startSpectatorBattle.bind(this)));
    this.screens.set(ScreenState.RECRUIT, new RecruitScreen(screenContext));
    this.screens.set(ScreenState.SHOP, new Shop(screenContext));
    this.screens.set(ScreenState.MAGE, new MageShop(screenContext));
    this.screens.set(ScreenState.SCHOOL, new TrainingSchool(screenContext));
    this.screens.set(ScreenState.BATTLE_SETUP, new BattleSetup(screenContext));
    this.screens.set(ScreenState.BATTLE, new Battle(screenContext));
    this.screens.set(ScreenState.OTHER_TEAMS, new TeamsScreen(screenContext));
    this.screens.set(ScreenState.SETTINGS, new Settings(screenContext));
    this.screens.set(ScreenState.BATTLE_SUMMARY, new BattleSummary(screenContext));
    this.screens.set(ScreenState.KILL_LEADERBOARD, new KillLeaderboard(screenContext));

    // Set up input handlers
    this.input.onMouseClick((x, y) => {
      if (this.currentScreen) {
        this.canvas.handleClick(x, y);
        this.currentScreen.handleClick(x, y);
      }
    });

    this.input.onKeyDown((key) => {
      if (this.currentScreen) {
        this.currentScreen.handleKeyDown(key);
      }
    });

    this.input.onWheel((x, y, deltaY) => {
      if (this.currentScreen?.handleWheel) {
        this.currentScreen.handleWheel(x, y, deltaY);
      }
    });

    // Start with title screen
    this.changeScreen(ScreenState.TITLESCREEN);

    console.log('Game initialized successfully');
  }

  start(): void {
    if (this.isRunning) return;

    console.log('Starting game loop...');
    this.isRunning = true;
    this.lastFrameTime = performance.now();
    requestAnimationFrame(this.gameLoop.bind(this));
  }

  stop(): void {
    this.isRunning = false;
  }

  private gameLoop(currentTime: number): void {
    if (!this.isRunning) return;

    const deltaTime = (currentTime - this.lastFrameTime) / 1000; // Convert to seconds
    this.lastFrameTime = currentTime;

    this.update(deltaTime);
    this.render();

    requestAnimationFrame(this.gameLoop.bind(this));
  }

  private update(deltaTime: number): void {
    if (this.currentScreen) {
      this.currentScreen.update(deltaTime);
    }
  }

  private render(): void {
    if (this.currentScreen) {
      this.canvas.clearClickableRegions();
      this.currentScreen.render(this.canvas);

      // Update and render tooltips (after screen render so they appear on top)
      this.canvas.updateTooltipHover();
      this.canvas.renderTooltip();
    }
  }

  changeScreen(newState: ScreenState): void {
    console.log(`Changing screen to ${ScreenState[newState]}`);

    // Run AI battles before showing summary so standings include all results
    if (newState === ScreenState.BATTLE_SUMMARY && this.gameState && isBattleMonth(this.gameState)) {
      runAIBattles(this.gameState);
    }

    // Exit current screen
    if (this.currentScreen) {
      this.currentScreen.exit();
    }

    // Get new screen
    const newScreen = this.screens.get(newState);
    if (!newScreen) {
      console.warn(`Screen ${ScreenState[newState]} not implemented yet`);
      return;
    }

    // Pass game state to screens that need it
    if (this.gameState && isGameStateScreen(newScreen)) {
      newScreen.setGameState(this.gameState);
    }

    // Enter new screen
    this.currentScreen = newScreen;
    this.currentScreen.enter();
  }

  startNewGame(playerTeamName: string): void {
    console.log(`Starting new game with team: ${playerTeamName}`);

    // Create fresh game state
    this.gameState = createGameState();

    // Initialize game with player team
    startNewGame(this.gameState, [playerTeamName], true);

    // Navigate to main menu
    this.changeScreen(ScreenState.MAIN_MENU);
  }

  progressMonth(): void {
    if (!this.gameState) return;

    console.log('Progressing to next month...');

    // AI battles already ran before showing BattleSummary (in changeScreen)

    // Progress time and handle monthly resets
    progressToNextMonth(this.gameState);

    // Refresh current screen to show updated state
    if (this.currentScreen && isGameStateScreen(this.currentScreen)) {
      this.currentScreen.setGameState(this.gameState);
    }

    console.log(`Now: ${this.gameState.month + 1}/${this.gameState.year}`);
  }

  async loadSavedGame(): Promise<boolean> {
    const loadedState = await loadGame();
    if (!loadedState) {
      console.log('No saved game found');
      return false;
    }

    this.gameState = loadedState;

    // Navigate to main menu
    this.changeScreen(ScreenState.MAIN_MENU);

    console.log('Game loaded successfully');
    return true;
  }

  async saveCurrentGame(): Promise<boolean> {
    if (!this.gameState) {
      console.log('No game state to save');
      return false;
    }

    const success = await saveGame(this.gameState);
    if (success) {
      console.log('Game saved successfully');
    }
    return success;
  }

  getAssets(): AssetManifest {
    return this.assets;
  }

  getCurrentScreen(): Screen | null {
    return this.currentScreen;
  }

  // Start a spectator battle (AI vs AI)
  startSpectatorBattle(): void {
    if (!this.gameState) {
      console.log('No game state - cannot start spectator battle');
      return;
    }

    const battleScreen = this.screens.get(ScreenState.BATTLE) as Battle | undefined;
    if (!battleScreen) {
      console.log('Battle screen not found');
      return;
    }

    // Set game state on battle screen
    battleScreen.setGameState(this.gameState);

    // Change to battle screen and initialize spectator battle
    this.changeScreen(ScreenState.BATTLE);
    battleScreen.initializeSpectatorBattle();
  }
}
